import { Alert, Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
  DataGridPremium,
  GridCellParams,
  GridColDef,
  GridToolbarContainer,
  GridToolbarContainerProps,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useContext, useMemo, useState } from 'react';

import { adminDataGridSx } from '@/components/admin/adminDataGridStyles';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { ApplicationCycleDataGridRow } from '@/models/Admin/AdminApplicationCycle/AdminApplicationCycle';
import { ApplicationCycle } from '@/models/ApplicationCycle';
import { GridLocaleTextOverride } from '@/utils/dataGridUtil';

import ApplicationCycleActions from './ApplicationCycleActions';
import EditableDateCell from './EditableDateCell';

type DayjsInput = string | number | Date | Dayjs | null | undefined;

const isDayjsInput = (value: unknown): value is DayjsInput => {
  if (value == null) return true;
  if (typeof value === 'string' || typeof value === 'number') return true;
  if (value instanceof Date) return true;
  return dayjs.isDayjs(value);
};

const toDateOnlyString = (value: DayjsInput): string | null => {
  if (value == null) return null;

  if (typeof value === 'string') {
    const isoDateMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDateMatch?.[1] != null) return isoDateMatch[1];
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

const toGridEditableDateValue = (value: DayjsInput): Date | null => {
  const dateOnly = toDateOnlyString(value);
  if (dateOnly == null) return null;

  // Use local noon so timezone offsets cannot roll the date to the previous/next day.
  return dayjs(`${dateOnly}T12:00:00`).toDate();
};

const AdminApplicationCycleDataGrid: React.FC<{
  rows: ApplicationCycleDataGridRow[];
  canEdit: boolean;
}> = ({ rows, canEdit }) => {
  const { locale } = useContext(LocaleContext);
  const apiRef = useGridApiRef();
  const [touchedRowIds, setTouchedRowIds] = useState<number[]>([]);
  const { setAlert } = useContext(SnackbarContext);

  const getErrorMessage = useCallback((error: unknown): string => {
    return error instanceof Error ? error.message : 'Unknown error';
  }, []);

  const markRowAsTouched = useCallback((rowId: number) => {
    setTouchedRowIds((prev) => (prev.includes(rowId) ? prev : [...prev, rowId]));
  }, []);

  const formatDateForDisplay = useCallback((value: unknown): string => {
    if (!isDayjsInput(value)) return '';

    const dateOnly = toDateOnlyString(value);
    if (dateOnly == null) return '';

    const dateValue = dayjs(dateOnly);
    return dateValue.isValid() ? dateValue.format('LL') : '';
  }, []);

  const sortDateComparator = useCallback((v1: unknown, v2: unknown): number => {
    const parseValue = (value: unknown): number => {
      if (!isDayjsInput(value)) return 0;

      const parsed = dayjs(value);
      return parsed.isValid() ? parsed.valueOf() : 0;
    };

    return parseValue(v1) - parseValue(v2);
  }, []);

  const normalizeDateForCompare = useCallback((value: unknown): string => {
    if (!isDayjsInput(value)) return '';

    return toDateOnlyString(value) ?? '';
  }, []);

  const hasCycleDateChanges = useCallback(
    (newRow: ApplicationCycleDataGridRow, oldRow: ApplicationCycleDataGridRow): boolean => {
      return (
        normalizeDateForCompare(newRow.openDate) !== normalizeDateForCompare(oldRow.openDate) ||
        normalizeDateForCompare(newRow.closeDate) !== normalizeDateForCompare(oldRow.closeDate)
      );
    },
    [normalizeDateForCompare]
  );

  const handleError = useCallback(
    (error: unknown) => {
      setAlert({
        message: `Error updating Application Cycle. Error: ${getErrorMessage(error)}`,
        severity: 'error',
      });
    },
    [getErrorMessage, setAlert]
  );

  const handleSuccess = useCallback(
    (applicationCycle: ApplicationCycle) => {
      setTouchedRowIds((prev) => prev.filter((id) => id !== applicationCycle.id));
      setAlert({
        message: 'Application Cycle updated successfully',
        severity: 'success',
      });
    },
    [setAlert]
  );

  const GridToolBarCustom = useCallback((props: GridToolbarContainerProps) => {
    return (
      <GridToolbarContainer {...props}>
        <GridToolbarFilterButton />

        <div style={{ flex: 1 }}></div>
        <GridToolbarQuickFilter debounceMs={1500} />
      </GridToolbarContainer>
    );
  }, []);

  const columns: GridColDef<ApplicationCycleDataGridRow>[] = useMemo(() => {
    return [
      { field: 'id', headerName: 'ID', flex: 1, editable: false },
      { field: 'program', headerName: 'Program', flex: 1, editable: false },
      { field: 'stateName', headerName: 'State', flex: 1, editable: false },
      {
        field: 'applicationCycleName',
        headerName: 'Name',
        flex: 1,
        editable: false,
      },
      {
        field: 'applicationCycleYear',
        headerName: 'Year',
        flex: 1,
        editable: false,
      },
      {
        field: 'openDate',
        headerName: 'Open Date',
        flex: 1,
        editable: canEdit,
        type: 'date',
        valueGetter: (value) => toGridEditableDateValue(value),
        valueFormatter: (value) => formatDateForDisplay(value),
        sortComparator: sortDateComparator,
        cellClassName: 'editable-date-cell',
        renderCell: (params) => <EditableDateCell text={formatDateForDisplay(params.value)} />,
      },
      {
        field: 'closeDate',
        headerName: 'Close Date',
        flex: 1,
        editable: canEdit,
        type: 'date',
        valueGetter: (value) => toGridEditableDateValue(value),
        valueFormatter: (value) => formatDateForDisplay(value),
        sortComparator: sortDateComparator,
        cellClassName: 'editable-date-cell',
        renderCell: (params) => <EditableDateCell text={formatDateForDisplay(params.value)} />,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0.5,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <ApplicationCycleActions
            params={params}
            touchedRowIds={touchedRowIds}
            handleSuccess={handleSuccess}
            handleError={handleError}
          />
        ),
      },
    ];
  }, [
    canEdit,
    formatDateForDisplay,
    handleError,
    handleSuccess,
    sortDateComparator,
    touchedRowIds,
  ]);

  const handleCellClick = useCallback(
    (params: GridCellParams<ApplicationCycleDataGridRow>) => {
      if (!canEdit || !params.isEditable || apiRef.current == null) return;

      const api = apiRef.current;

      if (api.getCellMode(params.id, params.field) === 'edit') return;

      api.startCellEditMode({ id: params.id, field: params.field });
    },
    [apiRef, canEdit]
  );

  return (
    <Box
      style={{
        backgroundColor: 'white',
        width: '80vw',
      }}
    >
      {canEdit && touchedRowIds.length > 0 && (
        <Box px={2} pt={2}>
          <Alert severity='warning'>
            {`You have ${touchedRowIds.length} unsaved application cycle change${
              touchedRowIds.length === 1 ? '' : 's'
            }. Click Save in the Actions column for each edited row.`}
          </Alert>
        </Box>
      )}
      <Box style={{ height: '50vh', width: '100%' }}>
        <DataGridPremium<ApplicationCycleDataGridRow>
          apiRef={apiRef}
          showToolbar
          localeText={GridLocaleTextOverride(locale)}
          columns={columns}
          rows={rows}
          sx={adminAppCycleDatagridSx}
          initialState={{
            density: 'compact',
            sorting: { sortModel: [{ field: 'Active', sort: 'desc' }] },
          }}
          pagination
          onCellClick={handleCellClick}
          processRowUpdate={
            canEdit
              ? (newRow, oldRow) => {
                  if (!hasCycleDateChanges(newRow, oldRow)) {
                    return oldRow;
                  }

                  markRowAsTouched(newRow.id);
                  return newRow;
                }
              : undefined
          }
          getRowClassName={(params) =>
            touchedRowIds.includes(Number(params.id)) ? 'app-cycle-unsaved-row' : ''
          }
          onProcessRowUpdateError={(error) => {
            handleError(error);
          }}
          slots={{
            toolbar: GridToolBarCustom,
          }}
        />
      </Box>
    </Box>
  );
};

const adminAppCycleDatagridSx = (theme: Theme) => ({
  ...adminDataGridSx(theme),
  marginLeft: '10px',
  marginRight: '10px',
  flex: '1 1 auto',
  '.grid-header-with-info': {
    display: 'flex',
    alignItems: 'space-between',
    justifyItems: 'center',
    gap: 1,
  },
  '& .bc-error': {
    backgroundColor: theme.palette.errorBackground.main,
    '&:hover': {
      backgroundColor: theme.palette.errorBackground.main,
    },
  },
});

export default AdminApplicationCycleDataGrid;
